import { captureException } from '@sentry/react';
import {
  applyIgniteInterceptors,
  type AxiosInstance,
  type AxiosRequestConfig,
  type BodyType,
  createCorrelationIdInterceptor,
  createIgniteAxiosClient,
  createIgniteAxiosClientInstance,
  type ErrorType,
} from '@alchemer/api';
import { useBoundStore } from '@/store';

/**
 * IgniteAxiosClient - Axios client for regional services (e.g., dashboard APIs)
 *
 * This client is used for API calls to services that are deployed per-region.
 * The baseURL is dynamically switched based on the user's selected region via
 * the getCurrentApiGatewayUrl() method from the store.
 *
 * Use this client for:
 * - Dashboard API calls (region-specific)
 * - Any services that are deployed per-region
 *
 * Do NOT use this client for:
 * - Global services (use GlobalAxiosClient instead)
 * - Services that should not be region-specific (e.g., impersonation)
 */

const BASE_URL =
  (typeof import.meta !== 'undefined' ? import.meta.env?.VITE_API_BASE_URL : undefined) ||
  process.env.VITE_API_BASE_URL ||
  '';

// Create an accessor function that will retrieve the token from Auth0 - used by our interceptors
let authTokenAccessor: () => Promise<string | null> | null = () => null;
let logoutActionHandler: (() => Promise<void>) | null = null;

/**
 * Set the accessor function to retrieve the auth token
 * @param accessor
 */
export const setAuthTokenAccessor = (accessor: () => Promise<string | null> | null) => {
  authTokenAccessor = accessor;
};

/**
 * Set the logout action to be called when a 401 error occurs
 * @param action
 */
export const setLogoutAction = (action: () => Promise<void>) => {
  logoutActionHandler = action;
};

// Create the axios instance with shared configuration
export const IgniteAxiosClientInstance: AxiosInstance = createIgniteAxiosClientInstance({
  baseURL: BASE_URL,
  withCredentials: true,
  defaultHeaders: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Create and apply correlation ID interceptor
// This must be added BEFORE applyIgniteInterceptors so it runs FIRST for errors (response error handlers run in order, not reverse)
const correlationIdInterceptor = createCorrelationIdInterceptor();
IgniteAxiosClientInstance.interceptors.response.use(
  correlationIdInterceptor.onFulfilled,
  correlationIdInterceptor.onRejected,
);

// Apply custom Ignite interceptors for auth token handling, error capturing, and logout action on 401 errors
applyIgniteInterceptors(IgniteAxiosClientInstance, {
  getAuthToken: () => authTokenAccessor && authTokenAccessor(),
  captureException: (message: string | Error, data?: Record<string, unknown>) => captureException(message, data),
  logoutAction: () => {
    if (logoutActionHandler) {
      return logoutActionHandler();
    }
    return Promise.resolve();
  },
});

// Add request interceptor to dynamically update baseURL from store
IgniteAxiosClientInstance.interceptors.request.use((config) => {
  const state = useBoundStore.getState();
  if (state.isRegionTransitioning) {
    return Promise.reject(new Error('Region is transitioning'));
  }

  const currentApiGatewayUrl = state.getCurrentApiGatewayUrl();
  if (currentApiGatewayUrl) {
    config.baseURL = currentApiGatewayUrl;
  }
  return config;
});

// Add interceptor to include impersonation header if impersonated user is set in the store
IgniteAxiosClientInstance.interceptors.request.use((config) => {
  const { impersonatedUser } = useBoundStore.getState();
  if (impersonatedUser) {
    config.headers['X-Alt-User-Context'] = impersonatedUser.email;
  }

  return config;
});

export const IgniteAxiosClient = <T>(config: AxiosRequestConfig): Promise<T> => {
  const TIMEOUT =
    parseInt((typeof import.meta !== 'undefined' ? import.meta.env?.VITE_AXIOS_TIMEOUT : undefined) as string, 10) ||
    30000;

  const client = createIgniteAxiosClient<T>({
    instance: IgniteAxiosClientInstance,
    timeout: TIMEOUT,
  });

  return client(config);
};

export type { ErrorType, BodyType };
